/**
 * Authentication service using AWS Amplify Auth v6
 * Provides authentication functions for Cognito user pool integration
 */

import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession as amplifyFetchAuthSession,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResendSignUpCodeInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from '@aws-amplify/auth';
import { Amplify } from 'aws-amplify';

// ============================================================================
// AMPLIFY CONFIGURATION
// ============================================================================

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const region = import.meta.env.VITE_AWS_REGION;

if (!userPoolId || !userPoolClientId || !region) {
  throw new Error(
    'Missing required Cognito configuration. Please set VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID, and VITE_AWS_REGION environment variables.'
  );
}

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId,
      userPoolClientId,
      loginWith: {
        email: true,
      },
    },
  },
});

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface SignInParams {
  email: string;
  password: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  name?: string;  // Deprecated
}

export interface ConfirmSignUpParams {
  email: string;
  confirmationCode: string;
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ConfirmForgotPasswordParams {
  email: string;
  confirmationCode: string;
  newPassword: string;
}

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
}

export interface AuthSession {
  accessToken: string;
  idToken: string;
}

// ============================================================================
// AUTHENTICATION FUNCTIONS
// ============================================================================

/**
 * Sign in a user with email and password
 */
export async function signIn({ email, password }: SignInParams): Promise<void> {
  const input: SignInInput = {
    username: email,
    password,
  };

  const result = await amplifySignIn(input);

  if (!result.isSignedIn) {
    throw new Error('Sign in failed');
  }
}

/**
 * Sign up a new user with email, password, and required names
 */
export async function signUp({ email, password, firstName, lastName }: SignUpParams): Promise<void> {
  const input: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        given_name: firstName,
        family_name: lastName,
      },
    },
  };

  const result = await amplifySignUp(input);

  if (!result.isSignUpComplete) {
    // User needs to confirm email
    console.log('Sign up successful. Please check your email for confirmation code.');
  }
}

/**
 * Confirm user sign up with verification code
 */
export async function confirmSignUp({ email, confirmationCode }: ConfirmSignUpParams): Promise<void> {
  const input: ConfirmSignUpInput = {
    username: email,
    confirmationCode,
  };

  await amplifyConfirmSignUp(input);
}

export interface ResendSignUpCodeParams {
  email: string;
}

/**
 * Resend the sign up confirmation code
 */
export async function resendSignUpCode({ email }: ResendSignUpCodeParams): Promise<void> {
  const input: ResendSignUpCodeInput = {
    username: email,
  };

  await amplifyResendSignUpCode(input);
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  await amplifySignOut();
}

/**
 * Initiate forgot password flow
 */
export async function forgotPassword({ email }: ForgotPasswordParams): Promise<void> {
  const input: ResetPasswordInput = {
    username: email,
  };

  await resetPassword(input);
}

/**
 * Confirm forgot password with verification code and new password
 */
export async function confirmForgotPassword({
  email,
  confirmationCode,
  newPassword,
}: ConfirmForgotPasswordParams): Promise<void> {
  const input: ConfirmResetPasswordInput = {
    username: email,
    confirmationCode,
    newPassword,
  };

  await confirmResetPassword(input);
}

/**
 * Get the current authenticated user
 * Returns null if not authenticated
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const user = await amplifyGetCurrentUser();

    return {
      userId: user.userId,
      email: user.signInDetails?.loginId || '',
      name: user.username,
    };
  } catch (error) {
    // User is not authenticated
    return null;
  }
}

/**
 * Fetch the current auth session with tokens
 * Returns null if not authenticated
 */
export async function fetchAuthSession(): Promise<AuthSession | null> {
  try {
    const session = await amplifyFetchAuthSession();

    if (!session.tokens) {
      return null;
    }

    return {
      accessToken: session.tokens.accessToken.toString(),
      idToken: session.tokens.idToken?.toString() || '',
    };
  } catch (error) {
    // No valid session
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser();
  return user !== null;
}

// ============================================================================
// PASSWORD MANAGEMENT
// ============================================================================

export interface ChangePasswordParams {
  currentPassword: string;
  newPassword: string;
}

/**
 * Change the current user's password
 */
export async function changePassword({
  currentPassword,
  newPassword,
}: ChangePasswordParams): Promise<void> {
  const { updatePassword } = await import('@aws-amplify/auth');

  await updatePassword({
    oldPassword: currentPassword,
    newPassword,
  });
}
